import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { alarmGridTheme } from './alarmGridTheme';
import type { CellClickedEvent, ColDef, ICellRendererParams, GridReadyEvent } from 'ag-grid-community';
import { History, Plus, RadioTower, Search } from 'lucide-react';
import { fetchActiveAlarms, fetchRawAlarmsPaged } from '@/api/alarms';
import type { ActiveAlarm, RawAlarm } from '@/api/alarms';
import { fetchPowerPlants } from '@/api/powerplants';
import TaskCreateModal from '@/pages/tasks/TaskCreateModal';
import type { TaskCreatePrefillValues } from '@/pages/tasks/TaskCreatePage';
import { useErrorToast } from '@/lib/notify';

type AlarmTab = 'active' | 'history';
type SeverityFilter = 'all' | 1 | 2 | 3;
const severityOptions = [
  { value: 'all', label: 'Tümü' },
  { value: 3, label: 'Majör' },
  { value: 2, label: 'Minör' },
  { value: 1, label: 'Uyarı' },
] as const;

const SEV: Record<number, { label: string; badge: string }> = {
  1: { label: 'Warning', badge: 'bg-blue-100 text-blue-700' },
  2: { label: 'Minor', badge: 'bg-amber-100 text-amber-700' },
  3: { label: 'Major', badge: 'bg-red-100 text-red-700' },
};

function PriorityCell({ value }: ICellRendererParams) {
  const s = SEV[value] ?? { label: 'Unknown', badge: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.badge}`}>
      {s.label}
    </span>
  );
}

function buildTaskDescription(alarm: ActiveAlarm | RawAlarm): string {
  const parts = [
    `Alarm Kaynagi: ${alarm.alarm_source_name}`,
    `Santral: ${alarm.alarm_power_plant_name}`,
    `Inverter: ${alarm.alarm_inverter_name}`,
    alarm.alarm_string_number ? `String: ${alarm.alarm_string_number}` : null,
    `Hata Kodu: ${alarm.alarm_code}`,
    `Aciklama: ${alarm.alarm_description}`,
  ];

  return parts.filter(Boolean).join('\n');
}

function buildTaskTitle(alarm: ActiveAlarm | RawAlarm): string {
  return `${alarm.alarm_power_plant_name} - ${alarm.alarm_code}`;
}

function toTaskPriority(priority: number): string {
  if (priority >= 3) return '3';
  if (priority === 2) return '2';
  return '1';
}

const HISTORY_COL_DEFS: ColDef<RawAlarm>[] = [
  { field: 'date_actual', headerName: 'Tarih', width: 120, filter: true },
  { field: 'alarm_source_name', headerName: 'Kaynak', width: 110, filter: true },
  { field: 'alarm_power_plant_name', headerName: 'Santral', flex: 1, filter: 'agTextColumnFilter' },
  { field: 'alarm_inverter_name', headerName: 'İnverter', flex: 1, filter: true },
  { field: 'alarm_string_number', headerName: 'String', width: 90, filter: true },
  {
    field: 'alarm_priority',
    headerName: 'Öncelik',
    width: 120,
    filter: true,
    cellRenderer: PriorityCell,
    comparator: (a, b) => a - b,
  },
  { field: 'alarm_code', headerName: 'Hata Kodu', width: 120, filter: true },
  { field: 'alarm_description', headerName: 'Açıklama', flex: 2, filter: true, tooltipField: 'alarm_description' },
];

const ACTIVE_COL_DEFS: ColDef<ActiveAlarm>[] = [
  { field: 'created_on', headerName: 'Son Alarm Zamanı', width: 155, sort: 'desc', filter: true },
  { field: 'alarm_source_name', headerName: 'Kaynak', width: 110, filter: true },
  { field: 'alarm_power_plant_name', headerName: 'Santral', flex: 1, filter: 'agTextColumnFilter' },
  { field: 'alarm_inverter_name', headerName: 'İnverter', flex: 1, filter: true },
  { field: 'alarm_string_number', headerName: 'String', width: 90, filter: true },
  {
    field: 'alarm_priority',
    headerName: 'Öncelik',
    width: 120,
    filter: true,
    cellRenderer: PriorityCell,
    comparator: (a, b) => a - b,
  },
  { field: 'alarm_code', headerName: 'Hata Kodu', width: 120, filter: true },
  { field: 'alarm_description', headerName: 'Açıklama', flex: 2, filter: true, tooltipField: 'alarm_description' },
  { field: 'status_description', headerName: 'İnverter Durumu', flex: 1, filter: true, tooltipField: 'status_description' },
];

export default function AlarmAltPage() {
  const headerActionsTarget = useHeaderActionsTarget();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plantUuid = searchParams.get('plant_uuid');
  const activeGridRef = useRef<AgGridReact<ActiveAlarm>>(null);
  const historyGridRef = useRef<AgGridReact<RawAlarm>>(null);
  const [activeTab, setActiveTab] = useState<AlarmTab>('active');
  const [historyRows, setHistoryRows] = useState<RawAlarm[]>([]);
  const [activeRows, setActiveRows] = useState<ActiveAlarm[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [activeLoading, setActiveLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [activeError, setActiveError] = useState('');
  useErrorToast(activeError);
  const [historyError, setHistoryError] = useState('');
  useErrorToast(historyError);
  const [plantFilterName, setPlantFilterName] = useState('');
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [taskInitialValues, setTaskInitialValues] = useState<TaskCreatePrefillValues | undefined>(undefined);

  const openTaskCreate = useCallback((alarm: ActiveAlarm | RawAlarm) => {
    setTaskInitialValues({
      powerplantName: alarm.alarm_power_plant_name,
      tasktitle: buildTaskTitle(alarm),
      taskdescription: buildTaskDescription(alarm),
      taskpriority: toTaskPriority(alarm.alarm_priority),
    });
    setTaskCreateOpen(true);
  }, []);

  const taskActionColDef = useCallback(
    <T extends ActiveAlarm | RawAlarm>(): ColDef<T> => ({
      colId: 'task_action',
      headerName: 'Görev',
      width: 140,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'right',
      lockPinned: true,
      cellRenderer: (params: ICellRendererParams<T>) => (
        <button
          type="button"
          data-task-create-trigger="true"
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (params.data) openTaskCreate(params.data);
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100"
          title="Görev Oluştur"
          aria-label="Görev Oluştur"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      ),
    }),
    [openTaskCreate],
  );

  const activeColDefs = useMemo(() => [...ACTIVE_COL_DEFS, taskActionColDef<ActiveAlarm>()], [taskActionColDef]);
  const historyColDefs = useMemo(() => [...HISTORY_COL_DEFS, taskActionColDef<RawAlarm>()], [taskActionColDef]);
  const filteredActiveRows = useMemo(() => activeRows.filter(row => severityFilter === 'all' || Number(row.alarm_priority) === severityFilter), [activeRows, severityFilter]);
  const filteredHistoryRows = useMemo(() => historyRows.filter(row => severityFilter === 'all' || Number(row.alarm_priority) === severityFilter), [historyRows, severityFilter]);

  useEffect(() => {
    let cancelled = false;
    setActiveLoading(true);
    setActiveError('');
    fetchActiveAlarms(plantUuid ? { plant_uuid: plantUuid } : undefined)
      .then(rows => { if (!cancelled) setActiveRows(rows); })
      .catch(() => { if (!cancelled) { setActiveRows([]); setActiveError('Aktif alarmlar yüklenemedi.'); } })
      .finally(() => { if (!cancelled) setActiveLoading(false); });
    return () => { cancelled = true; };
  }, [plantUuid]);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError('');
    fetchRawAlarmsPaged({
      range: '31days',
      all: true,
      plant_uuid: plantUuid || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        setHistoryRows(res.rows);
        setHistoryTotal(Number(res.total) || 0);
      })
      .catch(() => { if (!cancelled) { setHistoryRows([]); setHistoryTotal(0); setHistoryError('Alarm geçmişi yüklenemedi.'); } })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [plantUuid]);

  useEffect(() => {
    let cancelled = false;

    if (!plantUuid) {
      setPlantFilterName('');
      return () => {
        cancelled = true;
      };
    }

    fetchPowerPlants()
      .then((plants) => {
        if (cancelled) return;
        const plant = plants.find((item) => item.power_plant_uuid_id === plantUuid);
        setPlantFilterName(plant?.power_plant_name ?? '');
      })
      .catch(() => {
        if (!cancelled) setPlantFilterName('');
      });

    return () => {
      cancelled = true;
    };
  }, [plantUuid]);

  const applyPlantFilter = useCallback((api?: { setFilterModel: (model: unknown) => void }) => {
    if (!api) return;
    if (!plantUuid || !plantFilterName) {
      api.setFilterModel(null);
      return;
    }

    api.setFilterModel({
      alarm_power_plant_name: {
        filterType: 'text',
        type: 'equals',
        filter: plantFilterName,
      },
    });
  }, [plantUuid, plantFilterName]);

  const onGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit();
    applyPlantFilter(e.api as unknown as { setFilterModel: (model: unknown) => void });
  }, [applyPlantFilter]);

  useEffect(() => {
    applyPlantFilter(activeGridRef.current?.api as unknown as { setFilterModel: (model: unknown) => void } | undefined);
    applyPlantFilter(historyGridRef.current?.api as unknown as { setFilterModel: (model: unknown) => void } | undefined);
  }, [applyPlantFilter, activeRows, historyRows]);

  const onHistoryCellClicked = useCallback((e: CellClickedEvent<RawAlarm>) => {
    if (e.colDef.colId === 'task_action') return;
    const r = e.data;
    if (!r) return;
    const [dd, mm, yyyy] = r.date_actual.split('-');
    const isoDate = `${yyyy}-${mm}-${dd}`;
    const params = new URLSearchParams({
      date: isoDate,
      plant: r.alarm_power_plant_name,
      inverter: r.alarm_inverter_name,
      code: r.alarm_code,
      priority: String(r.alarm_priority),
      source: r.alarm_source_name,
      desc: r.alarm_description ?? '',
    });
    navigate(`/dashboard/alarms/detail?${params.toString()}`);
  }, [navigate]);

  const onActiveCellClicked = useCallback((e: CellClickedEvent<ActiveAlarm>) => {
    if (e.colDef.colId === 'task_action') return;
    const r = e.data;
    if (!r) return;
    const params = new URLSearchParams({
      date: r.date_iso,
      plant: r.alarm_power_plant_name,
      inverter: r.alarm_inverter_name,
      code: r.alarm_code,
      priority: String(r.alarm_priority),
      source: r.alarm_source_name,
      desc: r.alarm_description ?? '',
    });
    navigate(`/dashboard/alarms/detail?${params.toString()}`);
  }, [navigate]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerActionsTarget && createPortal(<div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          <label className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-slate-400">
            <Search size={14} className="shrink-0" />
            <input type="search" aria-label="Alarm listesinde ara" placeholder="Santral, inverter, hata kodu veya açıklama ara…" value={search} onChange={event => setSearch(event.target.value)}
              className="w-40 bg-transparent text-xs text-slate-700 outline-none xl:w-48" />
          </label>
      <div role="group" aria-label="Alarm dönemi" className="flex h-8 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        <button
          type="button"
          onClick={() => setActiveTab('active')}
          className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-[11px] font-semibold transition ${
            activeTab === 'active' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          <RadioTower className="h-3.5 w-3.5" />
          Aktif
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeTab === 'active' ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-600'}`}>
            {activeRows.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('history')}
          className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-[11px] font-semibold transition ${
            activeTab === 'history' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          <History className="h-3.5 w-3.5" />
          Geçmiş
          <span className={`rounded-full px-2 py-0.5 text-[11px] ${activeTab === 'history' ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-600'}`}>
            {historyTotal}
          </span>
        </button>
      </div>

          <div role="group" aria-label="Alarm önceliği" className="flex flex-wrap h-8 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {severityOptions.map(option => (
              <button key={option.value} type="button" aria-pressed={severityFilter === option.value} onClick={() => setSeverityFilter(option.value)}
                className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${severityFilter === option.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>
                {option.label}
              </button>
            ))}
          </div>
        </div>,headerActionsTarget)}
      <section aria-label="Alarm listesi" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {activeTab === 'active' ? (
        <>
          <div className="ag-theme-quartz min-h-0 flex-1 overflow-hidden rounded-xl shadow-sm">
            {activeLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">Yükleniyor...</div>
            ) : (
              <AgGridReact<ActiveAlarm>
                modules={[AllEnterpriseModule]}
                ref={activeGridRef}
                rowData={filteredActiveRows}
                quickFilterText={search}
                columnDefs={activeColDefs}
                defaultColDef={{ sortable: true, resizable: true, filter: true }}
                theme={alarmGridTheme}
                onGridReady={onGridReady}
                onCellClicked={onActiveCellClicked}
                rowClass="cursor-pointer"
                tooltipShowDelay={300}
              />
            )}
          </div>
        </>
      ) : (
        <>
          <div className="ag-theme-quartz min-h-0 flex-1 overflow-hidden rounded-xl shadow-sm">
            {historyLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">Yükleniyor...</div>
            ) : (
              <AgGridReact<RawAlarm>
                modules={[AllEnterpriseModule]}
                ref={historyGridRef}
                rowData={filteredHistoryRows}
                quickFilterText={search}
                columnDefs={historyColDefs}
                defaultColDef={{ sortable: true, resizable: true }}
                theme={alarmGridTheme}
                onGridReady={onGridReady}
                onCellClicked={onHistoryCellClicked}
                rowClass="cursor-pointer"
                tooltipShowDelay={300}
              />
            )}
          </div>
        </>
      )}
      </section>

      <TaskCreateModal
        open={taskCreateOpen}
        initialValues={taskInitialValues}
        onClose={() => {
          setTaskCreateOpen(false);
          setTaskInitialValues(undefined);
        }}
      />
    </div>
  );
}
